import { useEffect, useRef } from 'react';
import type { JSX } from 'react';
import type { InfoDoc } from './infoContent';

export interface InfoModalProps {
  /** 表示するドキュメント（タイトル + セクション）。 */
  readonly doc: InfoDoc;
  /** 閉じる操作のハンドラ。 */
  readonly onClose: () => void;
}

/**
 * 利用規約 / プライバシーポリシー / マニュアルなど、読み物系の文面を表示する
 * 汎用モーダル。`IconSettingsModal` と同じくネイティブ `<dialog>` を用いて
 * フォーカストラップ・backdrop・Esc クローズを OS 標準挙動に委ねる。
 */
export function InfoModal({ doc, onClose }: InfoModalProps): JSX.Element {
  const dialogRef = useRef<HTMLDialogElement>(null);

  // マウント時にモーダルとして開く / アンマウント時に閉じる。
  useEffect(() => {
    const dialog = dialogRef.current;
    if (dialog && !dialog.open) {
      dialog.showModal();
    }
    return () => {
      if (dialog && dialog.open) {
        dialog.close();
      }
    };
  }, []);

  // Esc (cancel) と backdrop クリックで閉じる。
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    const handleCancel = (e: Event) => {
      e.preventDefault();
      onClose();
    };
    const handleClick = (e: MouseEvent) => {
      if (e.target === dialog) {
        onClose();
      }
    };

    dialog.addEventListener('cancel', handleCancel);
    dialog.addEventListener('click', handleClick);
    return () => {
      dialog.removeEventListener('cancel', handleCancel);
      dialog.removeEventListener('click', handleClick);
    };
  }, [onClose]);

  return (
    <dialog ref={dialogRef} className="info-modal" aria-label={doc.title}>
      <div className="info-modal__content">
        <h2 className="info-modal__title">{doc.title}</h2>
        <div className="info-modal__body">
          {doc.sections.map((section, sIdx) => (
            <section key={sIdx} className="info-modal__section">
              {section.heading && (
                <h3 className="info-modal__heading">{section.heading}</h3>
              )}
              {section.body.map((paragraph, pIdx) => (
                <p key={pIdx} className="info-modal__paragraph">
                  {paragraph}
                </p>
              ))}
            </section>
          ))}
        </div>
        <div className="info-modal__actions">
          <button type="button" className="info-modal__close" onClick={onClose}>
            閉じる
          </button>
        </div>
      </div>
    </dialog>
  );
}
